import React, { ReactElement, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import BreedGuideCard from '../card/breed-guide-card';
import BreedGuideTabCard from '../card/breed-guide-tab-card';
import { Breed } from '../../../structures/models/breed';
import FANextArrow from './components/fa-next-arrow';
import FAPrevArrow from './components/fa-prev-arrow';

interface BreedCarouselProps {
  breed: Breed;
}

const BreedCarousel: React.FC<BreedCarouselProps> = (data): ReactElement => {
  const { breed } = data;
  const guides = [
    {
      title: 'Temperament',
      description: breed?.temperament_guide,
    },
    {
      title: 'Health',
      description: breed?.health_guide,
    },
    {
      title: 'Activity Level',
      description: breed?.energy_level_guide,
    },
    {
      title: 'Grooming',
      description: breed?.grooming_guide,
    },
    {
      title: 'Shedding',
      description: breed?.shedding_guide,
    },
    {
      title: 'Appearance',
      description: breed?.appearance_guide,
    },
    {
      title: 'History',
      description: breed?.history,
    },
  ].filter((guide) => !!guide.description);

  const setInitialStates = (guidesArray: Array<{ title: string, description: string|null }>) => {
    return { ...guidesArray.map((_) => false) };
  };

  const [activeStates, setActiveStates] = useState(setInitialStates(guides));
  const [slider1, setSlider1] = useState<Slider | undefined>(undefined);
  const [slider2, setSlider2] = useState<Slider | undefined>(undefined);

  const setActiveState = useCallback((key: number) => {
    const newActiveStates = { ...activeStates };
    newActiveStates[key] = !activeStates[key];
    setActiveStates(newActiveStates);
  }, [activeStates]);

  const fetchSlideNumber = (numSlides: number): number => {
    if (numSlides < 2) {
      return 1;
    }
    if (numSlides >= 2 && numSlides < 5) {
      return 2;
    }
    return 3;
  };

  // TODO: Update this depending on number of guides present
  const settings: Settings = {
    infinite: true,
    slidesToShow: fetchSlideNumber(guides.length),
    rows: guides.length > 2 ? 2 : 1,
    centerMode: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: guides.length >= 2 ? 2 : 1,
          rows: guides.length > 2 ? 2 : 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          rows: guides.length >= 2 ? 2 : 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const settingsTabs: Settings = {
    className: 'breed-guide-tab-slider',
    infinite: true,
    slidesToShow: fetchSlideNumber(guides.length),
    rows: guides.length > 2 ? 2 : 1,
    focusOnSelect: true,
    centerMode: false,
    nextArrow: <FANextArrow />,
    prevArrow: <FAPrevArrow />,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: guides.length >= 2 ? 2 : 1,
          rows: guides.length > 2 ? 2 : 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          rows: guides.length >= 2 ? 2 : 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <>
      <section className="carousel-header py-5 px-3">
        <h2 className="m-auto text-center pb-4">
          {breed?.guide_title || `${breed.name} Breed Guide`}
        </h2>
        <p className="mx-auto text-center pb-4 tagline">
          {breed?.guide_subtitle || `Learn More About ${breed.name}'s`}
        </p>
        <div className="container text-center" id="breed-tag-slider">
          <Slider
            {...settingsTabs}
            asNavFor={slider1}
            ref={(slider: Slider) => (setSlider2(slider))}
          >
            {guides.map((guide) => (
              <BreedGuideTabCard
                key={guide.title}
                title={guide.title}
              />
            ))}
          </Slider>
        </div>
        <div className="container text-center" id="breed-guide-slider">
          <Slider
            {...settings}
            asNavFor={slider2}
            ref={(slider: Slider) => (setSlider1(slider))}
          >
            {guides.map((guide, index) => (
              <BreedGuideCard
                key={guide.title}
                keyNum={index}
                isActive={activeStates[index]}
                title={guide.title}
                description={guide?.description || ''}
                activeToggleCallback={setActiveState}
              />
            ))}
          </Slider>
        </div>
      </section>
    </>
  );
};

export default BreedCarousel;

if (document.getElementsByClassName('breed-carousel').length > 0) {
  const element = document.getElementsByClassName('breed-carousel')[0];
  const breed = JSON.parse(element.getAttribute('data-breed') ?? '');
  ReactDOM.render(<BreedCarousel breed={breed} />, document.getElementsByClassName('breed-carousel')[0]);
}
